import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import Typography from '@src/new-components/Typography';
import { useSelector } from 'react-redux';
import moment from 'moment';
import CrownImg from '@src/assets/images/crown.png';
import route from '@src/constants/route';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import { Button } from '@mui/material';
import WaringImg from '@src/assets/images/warning.png';
import { delimitNumber } from '@src/utils/number';
import {
  checkCustomPackage,
  getDefaultValueCustomResource,
} from '@src/services/package';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  MOE_FEATURE_KEY,
  MOE_PAYWALL_CTA,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';

export const PRONUNCIATION_DIALOG_TYPE = {
  ERROR: 'ERROR',
  ONLY_VIETNAMESE: 'ONLY_VIETNAMESE',
  RECHECK_CONFIRMATION: 'RECHECK_CONFIRMATION',
  NOT_ENOUGH_CREDITS: 'NOT_ENOUGH_CREDITS',
  UPGRADE_PACKAGE: 'UPGRADE_PACKAGE',
  MAX_CHARS_EXCEEDED: 'MAX_CHARS_EXCEEDED',
};

// Must match the hit usePronunciationCheck fires when it opens the dialog
const PAYWALL_TYPE_BY_DIALOG = {
  [PRONUNCIATION_DIALOG_TYPE.UPGRADE_PACKAGE]: MOE_PAYWALL_TYPE.PREMIUM_AI,
  [PRONUNCIATION_DIALOG_TYPE.NOT_ENOUGH_CREDITS]:
    MOE_PAYWALL_TYPE.NOT_ENOUGH_CREDITS,
};

const BUTTON_40_STYLE = {
  padding: '8px 24px',
  borderRadius: '8px',
  height: 40,
};

const PronunciationCheckDialogs = ({
  dialogType,
  onClose,
  onConfirmRecheck,
  maxLengthInputText,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const history = useHistory();
  const { usingPackage, latestOrder } = useSelector((state) => state.user);
  const { handleGetTopUpPackageCode, handleGetPackageCodeMapping } =
    useMappingPackage();
  const { trackCta } = usePaywallTracking({
    paywallType: PAYWALL_TYPE_BY_DIALOG[dialogType],
    featureKey: MOE_FEATURE_KEY.PRONUNCIATION_CHECK,
  });

  const { packageExpiryDate } = latestOrder || {};
  const isFreePackage = FREE_PACKAGE_CODES.includes(usingPackage?.code);
  const isCustomPackage = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage?.level,
  });
  const hasExpired =
    packageExpiryDate && moment(packageExpiryDate).isBefore(moment());
  const showBuyMoreCredits = !isFreePackage && !isCustomPackage && !hasExpired;
  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
    concurrentRequestApi = 0,
  } = getDefaultValueCustomResource(usingPackage?.customResource) || {};

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.ERROR) {
    return (
      <CommonDialog
        open
        onClose={onClose}
        width="512px"
        icon={
          <WarningRoundedIcon
            sx={{ fontSize: 24, color: COLOR.textRejected }}
          />
        }
        title={t('pronunciationCheckErrorTitle')}
        contentComponent={
          <Typography variant="body-16-regular" color={COLOR.darkGray}>
            {t('pronunciationCheckErrorDesc')}
          </Typography>
        }
        actionComponent={
          <CommonButton
            variant="contained"
            onClick={onClose}
            customStyle={BUTTON_40_STYLE}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
          >
            {t('gotIt')}
          </CommonButton>
        }
        disableAutoClose
      />
    );
  }

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.ONLY_VIETNAMESE) {
    return (
      <CommonDialog
        open
        onClose={onClose}
        width="512px"
        icon={
          <WarningRoundedIcon
            sx={{ fontSize: 24, color: COLOR.textRejected }}
          />
        }
        title={t('pronunciationCheckErrorTitle')}
        contentComponent={
          <Typography variant="body-16-regular" color={COLOR.darkGray}>
            {t('pronunciationCheckOnlyVietnamese')}
          </Typography>
        }
        actionComponent={
          <CommonButton
            variant="contained"
            onClick={onClose}
            customStyle={BUTTON_40_STYLE}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
          >
            {t('gotIt')}
          </CommonButton>
        }
        disableAutoClose
      />
    );
  }

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.RECHECK_CONFIRMATION) {
    return (
      <CommonDialog
        width="512px"
        open
        onClose={onClose}
        icon={<WarningRoundedIcon sx={{ fontSize: 24, color: COLOR.alert }} />}
        title={t('noticePopup')}
        contentComponent={
          <Typography variant="body-16-regular" color={COLOR.darkGray}>
            {t('pronunciationCheckRecheckWarningDesc')}
          </Typography>
        }
        actionComponent={
          <>
            <CommonButton
              variant="outlined"
              onClick={onClose}
              customStyle={BUTTON_40_STYLE}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={onConfirmRecheck}
              customStyle={BUTTON_40_STYLE}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
            >
              {t('continue')}
            </CommonButton>
          </>
        }
        disableAutoClose
      />
    );
  }

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.UPGRADE_PACKAGE) {
    return (
      <ActionDialog
        open
        onClose={onClose}
        title={t('pronunciationCheckUnlockFeatureTitle')}
        styleTitle={{ textAlign: 'center' }}
        contentComponent={
          <Typography
            style={{ textAlign: 'center' }}
            variant="body-16-regular"
            color={COLOR.descriptionGray}
            dangerouslySetInnerHTML={{
              __html: t('pronunciationCheckUnlockFeatureDesc'),
            }}
          />
        }
        fullWidth
        image={CrownImg}
        imageContainerSize="100px"
        width="640px"
        actionComponent={
          <CommonButton
            variant="contained"
            onClick={() => {
              trackCta(MOE_PAYWALL_CTA.UPGRADE);
              onClose();
              history.push(route.PAYMENT);
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              ...BUTTON_40_STYLE,
              padding: '8px 16px',
            }}
          >
            {t('buyNow')}
          </CommonButton>
        }
      />
    );
  }

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.NOT_ENOUGH_CREDITS) {
    return (
      <ActionDialog
        open
        onClose={onClose}
        title={t('checkNotEnoughCreditsTitle')}
        styleTitle={{ textAlign: 'center' }}
        contentComponent={
          <Typography
            style={{ textAlign: 'center' }}
            variant="body-16-regular"
            color={COLOR.descriptionGray}
            dangerouslySetInnerHTML={{
              __html: t('checkNotEnoughCreditsDesc'),
            }}
          />
        }
        fullWidth
        image={CrownImg}
        imageContainerSize="100px"
        width="640px"
        isSingleActionButton={!showBuyMoreCredits}
        actionComponent={
          <>
            {showBuyMoreCredits && (
              <CommonButton
                variant="outlined"
                onClick={() => {
                  onClose();
                  const topUpPackageCode = handleGetTopUpPackageCode({
                    type: usingPackage?.type,
                    level: usingPackage?.level,
                  });
                  trackCta(MOE_PAYWALL_CTA.TOPUP, {
                    targetPackageCode: topUpPackageCode,
                  });
                  history.push(`${route.PAYMENT}?package=${topUpPackageCode}`);
                }}
                textProps={{
                  variantText: 'body-14-semibold',
                  color: COLOR.darkBlue,
                }}
                customStyle={{
                  ...BUTTON_40_STYLE,
                  padding: '8px 16px',
                }}
              >
                {t('buyMoreCredits')}
              </CommonButton>
            )}
            <CommonButton
              variant="contained"
              onClick={() => {
                onClose();
                const upgradePackage = handleGetPackageCodeMapping({
                  level: usingPackage?.level,
                  type: usingPackage?.type,
                  expiresIn: usingPackage?.expiresIn,
                });

                const redirectRoute = isCustomPackage
                  ? `${route.PAYMENT}?package=${upgradePackage}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}&concurrentRequestApi=${concurrentRequestApi}`
                  : `${route.PAYMENT}?package=${upgradePackage}`;
                trackCta(MOE_PAYWALL_CTA.UPGRADE, {
                  targetPackageCode: upgradePackage,
                });
                history.push(redirectRoute);
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                ...BUTTON_40_STYLE,
                padding: '8px 16px',
              }}
            >
              {t('buyNow')}
            </CommonButton>
          </>
        }
      />
    );
  }

  if (dialogType === PRONUNCIATION_DIALOG_TYPE.MAX_CHARS_EXCEEDED) {
    return (
      <NotificationDialogV4
        open
        onClose={onClose}
        image={WaringImg}
        title={t('maxCharsLimitExceeded')}
        description={t('pronunciationCheckMaxCharsDesc', {
          maxLengthInputText: delimitNumber(maxLengthInputText, language),
        })}
        actionComponent={
          <Button variant="contained" onClick={onClose}>
            {t('understood')}
          </Button>
        }
      />
    );
  }

  return null;
};

export default PronunciationCheckDialogs;
